import { BiSearchIconInput } from "../../primitives/inputs/BiSearchIconInput.primitive";

import {
  Group,
  Row,
  SorterIcon,
  Table,
  TableData,
  TableFooter,
  TableHeading,
  TableRow,
  TableHead,
  TableBody,
  NoDataTableRow,
  NoDataTableCell,
} from "./TemplateTableStyles";
import Sorter from "../../../assets/sorter.svg";
import SorterAsc from "../../../assets/sorter-asc.svg";
import SorterDesc from "../../../assets/sorter-desc.svg";
import { ITableHeader } from "./models/ITableHeader";
import Paginator from "../paginator/Paginator";
import { useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import { setToastData } from "../../../app/toastReducer";
import { AppConstants } from "../../../constants/AppConstants";
import Spinner from "../Spinner/Loader";

interface TemplateProps {
  tableData: any;
  onRefreshTableData: any;
  size: string;
  currentPage: string;
  templateSelector: any;
  cellSelector?: Function;
  tableHeaders: Array<ITableHeader>;
  setTableHeaders: any;
  searchPlaceholder?: string;
  pagingData?: any;
  containerClass?: string;
  isPaginated?: boolean;
  isSearchable?: boolean;
  setSize?: Function;
  setCurrentPage?: any;
  tableName?: string;
  isLoading?: boolean;
  tableBodyClass?: string;
  theme?: any;
}

const TemplateTable: React.FC<TemplateProps> = ({
  tableData,
  size,
  currentPage,
  onRefreshTableData,
  templateSelector,
  cellSelector = undefined,
  tableHeaders,
  setTableHeaders,
  searchPlaceholder = "Search Name",
  pagingData,
  containerClass,
  isPaginated = true,
  isSearchable = true,
  setSize,
  setCurrentPage,
  tableName,
  isLoading,
  tableBodyClass,
  theme,
}: TemplateProps) => {
  const location = useLocation().pathname;
  const [searchText, setSearchText] = useState("");
  const [sortType, setSortType] = useState("desc");
  const [sortName, setSortName] = useState("createdOn");
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const sizeChange = (page: string, size: string) => {
    onRefreshTableData(searchText, currentPage, size, sortName, sortType);
  };

  const debouncedSearch = useRef(
    debounce(async (data) => {
      if (data && data.trim()) {
        await onRefreshTableData(
          data.trim(),
          currentPage,
          size,
          sortName,
          sortType
        );
      } else if (!data) {
        await onRefreshTableData(data, currentPage, size, sortName, sortType);
      }
    }, 400)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    if (e.target.value.length < AppConstants.DEFAULT_TEXT_MAX_LENGTH) {
      debouncedSearch(e.target.value);
    } else {
      dispatch(
        setToastData({
          toastMessage: t("T_SEARCH_ERROR"),
          isToastActive: true,
          toastType: "error",
        })
      );
    }
  };

  return (
    <div className="h-full">
      {isSearchable && (
        <Row justifyContent="flex-start" className="float-right">
          <Group width="335">
            <BiSearchIconInput
              isEmailTemplate
              autoFocus
              value={searchText}
              placeholder={searchPlaceholder}
              onChange={handleChange}
            />
          </Group>
        </Row>
      )}
      <div className="w-full pb-1" style={{ height: "inherit" }}>
        <Table width="100%" className={`flex h-fit max-h-full flex-col`}>
          <TableHead className="flex-initial">
            <TableRow key={"header-row"}>
              {tableHeaders.map((tableHeader, index: number) => {
                return (
                  <TableHeading
                    key={`${tableHeader.key}-${index}`}
                    width={tableHeader.width}
                    className={`${
                      (tableName === `${t("T_USER_PERMISSION")}` &&
                        index === 1) ||
                      (tableName === `${t("T_TABLE_APP_PERMISSION_NAME")}` &&
                        index === 3)
                        ? "justify-center"
                        : ""
                    }`}
                    style={tableHeader.styles}
                  >
                    {t(tableHeader.description)}
                    {tableHeader.sortEnabled ? (
                      <SorterIcon
                        onClick={() => {
                          // sort(tableHeader, index)
                          let sortTypeTemp: any = "";
                          if (!tableHeader.sortingInformation?.order) {
                            sortTypeTemp = "desc";
                          }
                          if (
                            tableHeader.sortingInformation?.order === "desc"
                          ) {
                            sortTypeTemp = "asc";
                          }

                          setSortType(sortTypeTemp);
                          setSortName(tableHeader.key);

                          let tableHeadersTemp = [...tableHeaders];
                          tableHeadersTemp[index].sortingInformation!.order =
                            sortTypeTemp;

                          setTableHeaders(tableHeadersTemp);
                          if (sortTypeTemp) {
                            onRefreshTableData(
                              searchText,
                              currentPage,
                              size,
                              tableHeader.key,
                              sortTypeTemp
                            );
                          } else {
                            onRefreshTableData(searchText, currentPage, size);
                          }
                        }}
                        src={
                          tableHeader.sortingInformation?.order === "desc"
                            ? SorterDesc
                            : tableHeader.sortingInformation?.order === "asc"
                            ? SorterAsc
                            : Sorter
                        }
                        alt="sorterImg"
                      />
                    ) : (
                      ""
                    )}
                  </TableHeading>
                );
              })}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableBody className="flex h-full w-full items-center justify-center py-12">
              <TableRow>
                <td>
                  <div>
                    <Spinner height="40px" width="40px"></Spinner>
                  </div>
                </td>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody
              className={`h-full flex-auto overflow-auto
               ${tableBodyClass}`}
              key="table_body"
            >
              {typeof tableData === "string" ? (
                <NoDataTableRow key={"NoDataTableRow"}>
                  <NoDataTableCell width="100%">{tableData}</NoDataTableCell>
                </NoDataTableRow>
              ) : (
                tableData?.map((element: any, index: number) => {
                  return element.loading ? (
                    <TableRow key={"spinnerTableRow"}>
                      <td
                        key={index}
                        data-testid="delete-loader"
                        className=" flex h-[40px] w-full items-center justify-center py-10"
                      >
                        <Spinner height="40px" width="40px"></Spinner>
                      </td>
                    </TableRow>
                  ) : (
                    <TableRow
                      data-testid="table-row"
                      key={index}
                      onClick={() => {
                        if (!cellSelector) templateSelector(element, 2);
                      }}
                      hover={false}
                      style={{ overflowWrap: "anywhere" }}
                    >
                      {tableHeaders.map((header) => {
                        return (
                          <TableData
                            key={header.key}
                            width={header.width}
                            onClick={() => {
                              if (cellSelector && header.clickable)
                                cellSelector(element, header.key);
                            }}
                            notCursor={header?.styles?.notCursor}
                            hover={header.key === "contract" ? true : false}
                            style={header?.styles?.rowStyle}
                            className={
                              header.key === "contract"
                                ? "overflow-hidden text-ellipsis"
                                : ""
                            }
                            // role={header.key === 'contract' ? "contract-file" : " "}
                            data-testid="table-data-item"
                            // aria-label={`file`}
                          >
                            {header.render
                              ? header.render(element, index)
                              : element && element[header.key]}
                          </TableData>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          )}
          {isPaginated && (
            <TableFooter className="flex-1">
              <TableRow key={"footer"} hover={true}>
                <TableData
                  key={"TableFooter"}
                  hover={true}
                  width={"100%"}
                  style={{ paddingTop: "7px", paddingBottom: "7px" }}
                >
                  <Paginator
                    onSizeChanged={sizeChange}
                    size={size}
                    currentPage={currentPage}
                    pagingData={pagingData}
                    refreshPaginatedData={(page: number) => {
                      onRefreshTableData(
                        searchText,
                        page,
                        size,
                        sortName,
                        sortType
                      );
                    }}
                    containerClass={containerClass ? containerClass : "py-5"}
                    theme={theme}
                  ></Paginator>
                </TableData>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
};

export default TemplateTable;
